/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Bar } from './Bar';
import { BarItemData, BarSeriesData, StackLabelContext } from './barChart.types';
import { getBarProps, getRectCoord } from './utils/barItemUtils';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { getBarGapsInfo } from './utils/barSeriesUtils';
import { getBaseAndEndCoord } from './utils/barLayoutUtils';
import { getMixedFreqGroupCenter } from '../utils/PRIVATE_chartUtils/layoutUtils';
import { Info, Scale, Group } from '../utils/UNSAFE_visTypes/chart';
import { Dimension } from '../utils/UNSAFE_visTypes/common';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { getItemAriaLabel } from '../utils/PRIVATE_chartUtils/accUtils';
import { VisTextStyle, supportsMobileScreenReader } from '../utils/UNSAFE_visUtils';
import { Font } from '../hooks/PRIVATE_useTextDimensions';
import { StackLabels } from './BarChartStackLabel';
import { BarLabel } from './BarLabel';

type BarGroupProps<K, D> = {
  yScale: Scale;
  xScale: Scale;
  colors: ColorProps['color'][];
  series: BarSeriesData<K, D>[];
  group: Group;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
  focusedItemInfo?: Info;
  hoveredItemInfo?: Info;
  isSelectionEnabled: boolean;
  selectedIds?: Set<K>;
  hiddenIds?: Set<K>;
  highlightedIds?: Set<K>;
  groupIndex: number;
  barGapRatio: number;
  dataItemGaps: number;
  groupWidth: number;
  isStacked: boolean;
  activeId?: string;
  orientation: 'horizontal' | 'vertical';
  isDrillEnabled: boolean;
  isLog: boolean;
  isRtl: boolean;
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  isPointInsideMarquee: ((id: K, point: { x: number; y: number }) => boolean) | undefined;
  getTextDimensions?: (text: string, style: Font) => Dimension;
  defaultFontSize: string;
  stacklabelStyle?: VisTextStyle;
  getStackLabel?: (context: StackLabelContext<D>) => string;
};

export function BarGroup<K extends string | number, D extends BarItemData<K>>({
  yScale,
  xScale,
  colors,
  series,
  groupIndex,
  hiddenIds,
  barGapRatio,
  dataItemGaps,
  groupWidth,
  highlightedIds,
  selectedIds,
  isPointInsideMarquee,
  orientation,
  isLog,
  activeId,
  getDataItem,
  isRtl,
  isStacked,
  isSelectionEnabled,
  focusedItemInfo,
  hoveredItemInfo,
  isDrillEnabled,
  group,
  timeAxisType,
  getTextDimensions,
  defaultFontSize,
  stacklabelStyle,
  getStackLabel
}: BarGroupProps<K, D>) {
  const isHoriz = orientation === 'horizontal';
  const items = [];
  const numSeries = series.length;

  let posCumulativeValue = isLog ? yScale.domain()[0] : 0;
  let negCumulativeValue = 0;

  const positiveValueItems: D[] = [];
  const negativeValueItems: D[] = [];

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const isStackLabelRendered = isStacked && !!getStackLabel && timeAxisType != 'mixedFrequency';

  let groupCenter =
    timeAxisType != 'mixedFrequency'
      ? xScale.transform(timeAxisType != undefined ? new Date(group.id).getTime() : groupIndex)
      : undefined;

  for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex += 1) {
    const item = getDataItem(seriesIndex, groupIndex);
    if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
      continue;
    }

    if (timeAxisType === 'mixedFrequency') {
      groupCenter = getMixedFreqGroupCenter<K, D>(xScale, item);
    }

    const isFocused =
      focusedItemInfo?.groupIndex === groupIndex &&
      focusedItemInfo?.seriesIndex === seriesIndex &&
      focusedItemInfo?.isFocusVisible;
    const isHovered =
      hoveredItemInfo?.groupIndex === groupIndex &&
      hoveredItemInfo?.seriesIndex === seriesIndex &&
      hoveredItemInfo?.isCurrent;
    const isFocusVisible = !!isFocused || (!!isHovered && isSelectionEnabled);
    const isActive = (isFocused && focusedItemInfo.isCurrent) || isHovered;
    const isHighlighted = highlightedIds === undefined || highlightedIds.has(item.id);

    const isDrillable = item.drilling === 'on' || (item.drilling != 'off' && isDrillEnabled);

    const { barWidth, offset, itemsGaps } = getBarGapsInfo(
      seriesIndex,
      groupWidth,
      barGapRatio,
      numSeries,
      dataItemGaps,
      isStacked,
      isRtl
    );
    const x1 = groupCenter! + offset;
    const x2 = x1 + barWidth;

    const { baselineCoord, endCoord } = getBaseAndEndCoord(
      item.value,
      yScale,
      isStacked,
      isLog,
      itemsGaps,
      negCumulativeValue,
      posCumulativeValue
    );

    negCumulativeValue += item.value < 0 ? item.value : 0;
    posCumulativeValue += item.value > 0 ? item.value : 0;

    if (isStackLabelRendered) {
      if (item.value >= 0) {
        positiveValueItems.push(item);
      } else {
        negativeValueItems.push(item);
      }
    }

    const barprops = getBarProps(item, series[seriesIndex], colors?.[seriesIndex]);
    const rectParam = getRectCoord(x1, x2, endCoord, baselineCoord, isHoriz);
    const isSelected = isPointInsideMarquee
      ? isPointInsideMarquee?.(item.id, {
          x: rectParam.x + rectParam.width / 2,
          y: rectParam.y
        })
      : selectedIds?.has(item.id!);

    const ariaLabel =
      supportsMobileScreenReader || isActive
        ? getItemAriaLabel(
            translations,
            series[seriesIndex].name || series[seriesIndex].id.toString(),
            group.name || group.id,
            item.value,
            isSelectionEnabled,
            !!isSelected,
            isDrillable,
            item.accessibleLabel
          )
        : '';

    items.push(
      <>
        <Bar
          {...rectParam}
          {...barprops}
          seriesIndex={seriesIndex}
          groupIndex={groupIndex}
          isFocused={isFocusVisible}
          activeId={isActive ? activeId : ''}
          aria-label={ariaLabel}
          isDimmed={!isHighlighted}
          isSelected={!!isSelected}
          isPointerEnabled={isDrillable || isSelectionEnabled}
          key={`${groupIndex};${seriesIndex}`}
        />
        <BarLabel
          item={item}
          isRtl={isRtl}
          seriesColor={colors[seriesIndex % 12]}
          isStacked={isStacked}
          defaultFontSize={defaultFontSize}
          barDim={rectParam}
          isHoriz={isHoriz}
          getTextDimensions={getTextDimensions}
        />
      </>
    );
  }

  return (
    <>
      {items}
      {isStackLabelRendered && getTextDimensions && (
        <StackLabels
          group={group}
          isRtl={isRtl}
          yScale={yScale}
          getStackLabel={getStackLabel}
          defaultFontSize={defaultFontSize}
          getTextDimensions={getTextDimensions}
          posItemsData={positiveValueItems}
          negItemsData={negativeValueItems}
          isHoriz={isHoriz}
          groupCenter={groupCenter!}
          labelStyle={stacklabelStyle}
          posValue={posCumulativeValue}
          negValue={negCumulativeValue}
        />
      )}
    </>
  );
}
